import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trophy, Users, Copy, CheckCircle2, ChevronDown, Beer, Clock, Lock, Coins, Wallet, Download, Target } from "lucide-react";
import { api, apiDownload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InvitesManager from "@/components/InvitesManager";
import NotifyBox from "@/components/NotifyBox";
import { GiocataGrid } from "@/components/GiocataGrid";
import GridOddsAdmin from "@/components/GridOddsAdmin";
import CalcolaStanza from "@/components/CalcolaStanza";
import BonusStanzaAdmin from "@/components/BonusStanzaAdmin";

export default function TiketRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [room, setRoom] = useState(null);
  const [board, setBoard] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);
  const [gridKey, setGridKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoom(await api(`/rooms/${roomId}`));
      setBoard(await api(`/rooms/${roomId}/leaderboard`).catch(() => null));
      setMembers(await api(`/rooms/${roomId}/members`).catch(() => []));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [roomId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !room) return <div className="py-16 text-center text-[#94A3B8]">Caricamento...</div>;
  const admin = isAdmin || room.is_admin;
  const entries = board?.leaderboard || [];
  const hasResults = !!board?.has_results;
  const montepremi = board?.montepremi || 0;
  const payout = board?.payout;

  const saveStake = async (membershipId, value) => {
    const v = Number(value);
    if (isNaN(v) || v < 0) { toast.error("Puntata non valida"); return; }
    try {
      await api(`/rooms/${roomId}/members/${membershipId}/stake`, { method: "PATCH", body: { stake: v } });
      toast.success("Puntata aggiornata");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const downloadPdf = async () => {
    try {
      await apiDownload(`/rooms/${roomId}/premi/pdf`, {}, `premi_${(room.name || "stanza").replace(/\s+/g, "_")}.pdf`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5">
      <button data-testid="tkr-back" onClick={() => navigate("/tiket")} className="flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Stanze</button>

      <div className="rounded-xl border border-white/10 bg-[#181D22] p-4">
        <div className="text-2xl font-extrabold">{room.name}</div>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#94A3B8]">
          <span>{room.description || (room.matchday != null ? `Giornata ${room.matchday}` : "Ticket libero")}</span>
          <span>Max eventi: {room.max_events}</span>
          {room.status && <span data-testid="tkr-room-status" className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${room.status === "settled" ? "bg-[#00D95F]/20 text-[#00D95F]" : "bg-white/10 text-[#94A3B8]"}`}>{room.status === "settled" ? "Concluso" : room.status === "closed" ? "Chiusa" : "Aperta"}</span>}
          {room.partial && <span data-testid="tkr-partial-badge" className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#3B82F6]/15 text-[#93C5FD] border border-[#3B82F6]/40"><Clock size={12} /> Calcolo parziale · in corso{room.pending_count > 0 ? ` (${room.pending_count} in attesa)` : ""}</span>}
        </div>
        {room.invite_code && admin && (
          <button data-testid="tkr-copy-code" onClick={() => { navigator.clipboard?.writeText(room.invite_code); toast.success("Codice copiato"); }} className="mt-3 inline-flex items-center gap-2 text-xs bg-white/10 rounded-md px-3 py-1.5">
            <Copy size={13} /> Codice invito: <b>{room.invite_code}</b>
          </button>
        )}
      </div>

      {admin && <InvitesManager basePath={`/rooms/${roomId}`} showStake />}

      {/* Griglia giocata (collegata al backend) */}
      <GiocataGrid
        key={gridKey}
        roomId={roomId}
        isAdmin={admin}
        members={members}
        onSubmitted={load}
      />

      {/* Admin: gestione quote della griglia */}
      {admin && <GridOddsAdmin roomId={roomId} onSaved={() => { setGridKey((k) => k + 1); load(); }} />}

      {/* Admin: calcolo risultati e premi della stanza (indipendente) */}
      {admin && <CalcolaStanza roomId={roomId} settled={room.status === "settled"} onSettled={load} />}

      {/* Admin: bonus Big Match della stanza */}
      {admin && <BonusStanzaAdmin roomId={roomId} onChanged={load} />}

      {/* Partecipanti + stato consegna schedina */}
      <div>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h2 className="text-lg font-extrabold flex items-center gap-2"><Users size={18} className="text-[#F59E0B]" /> Partecipanti ({members.length})</h2>
          {montepremi > 0 && !payout && (
            <span data-testid="tkr-montepremi" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#00D95F] bg-[#00D95F]/10 border border-[#00D95F]/30 rounded-full px-3 py-1"><Wallet size={14} /> Montepremi {montepremi.toFixed(2)}€</span>
          )}
        </div>
        <div className="rounded-xl border border-white/10 bg-[#181D22] divide-y divide-white/10">
          {members.length === 0 && <div className="px-4 py-6 text-center text-[#94A3B8]">Nessun partecipante.</div>}
          {members.map((m) => (
            <div key={m.membership_id || m.user_id} data-testid={`tkr-member-${m.user_id}`} className="px-4 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#F59E0B]/20 flex items-center justify-center text-xs font-bold text-[#F59E0B] shrink-0">{(m.nickname || "?").slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{m.nickname}</div>
                {admin ? (
                  <div className="flex items-center gap-1 mt-1">
                    <Coins size={12} className="text-[#F59E0B]" />
                    <input
                      data-testid={`tkr-stake-${m.membership_id}`}
                      type="number" min="0" step="0.5"
                      defaultValue={m.stake || 0}
                      onBlur={(e) => { if (Number(e.target.value) !== (m.stake || 0)) saveStake(m.membership_id, e.target.value); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="w-20 bg-[#0F1216] border border-white/15 rounded px-2 py-1 text-xs text-white"
                    />
                    <span className="text-[10px] text-[#64748B]">€ puntata</span>
                  </div>
                ) : (
                  m.stake > 0 && <div className="text-xs text-[#94A3B8] flex items-center gap-1"><Coins size={11} className="text-[#F59E0B]" /> Puntata {m.stake}€</div>
                )}
              </div>
              {m.submitted ? (
                <span data-testid={`tkr-badge-ok-${m.user_id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-[#00D95F]/15 text-[#00D95F] border border-[#00D95F]/40"><CheckCircle2 size={13} /> Giocata effettuata</span>
              ) : (
                <span data-testid={`tkr-badge-pending-${m.user_id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-white/5 text-[#94A3B8] border border-white/10"><Clock size={13} /> In attesa</span>
              )}
            </div>
          ))}
        </div>
        {admin && <p className="text-xs text-[#64748B] mt-2">Imposta la puntata di ogni giocatore (Invio o clic fuori per salvare). Per giocare al posto di un partecipante usa "Gioca come" nella griglia.</p>}
      </div>

      {admin && <NotifyBox userIds={members.map((m) => m.user_id)} url={`/tiket/${roomId}`} />}

      <div>
        <h2 className="text-lg font-extrabold mb-2 flex items-center gap-2"><Trophy size={18} className="text-[#F59E0B]" /> Classifica</h2>
        <p className="text-xs text-[#94A3B8] mb-2">Tocca un giocatore per vedere la sua schedina.</p>

        {board?.bonus?.enabled && (
          <div data-testid="tkr-bonus-line" className="rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 px-3 py-2 mb-3 text-sm flex items-center gap-2 flex-wrap">
            <Target size={14} className="text-[#10B981]" />
            <span className="font-bold text-[#10B981]">Bonus Big Match:</span>
            <span>{board.bonus.home} - {board.bonus.away}</span>
            {board.bonus.result && <span className="text-[#F59E0B] font-bold">· Risultato {board.bonus.result.home}-{board.bonus.result.away}</span>}
            {board.bonus.result && <span className="text-[#94A3B8]">· {board.bonus.winners.length ? `Vincitori: ${board.bonus.winners.join(", ")}` : "Nessun vincitore"}</span>}
            <span className="text-[#64748B] text-xs">(si gioca nella sezione Bonus)</span>
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-[#181D22] divide-y divide-white/10">
          {entries.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">{hasResults ? "Nessuna schedina consegnata." : "Risultati non ancora inseriti: l'admin deve caricare i risultati della giornata."}</div>
          ) : entries.map((e, i) => {
            const isLast = i === entries.length - 1;
            const rowKey = e.membership_id || e.user_id || i;
            const open = openRow === rowKey;
            return (
              <div key={rowKey} data-testid={`tkr-row-${e.user_id || i}`}>
                <button onClick={() => setOpenRow(open ? null : rowKey)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors">
                  <span className="w-6 text-[#94A3B8] font-bold">{e.rank || i + 1}</span>
                  <div className="w-8 h-8 rounded-full bg-[#F59E0B]/20 flex items-center justify-center text-xs font-bold text-[#F59E0B] shrink-0">{(e.nickname || "?").slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.nickname}</div>
                    {e.played === false
                      ? <div className="text-xs text-[#64748B]">Non giocata{e.stake > 0 ? ` · punt. ${e.stake}€` : ""}</div>
                      : e.hidden
                      ? <div className="text-xs text-[#64748B] flex items-center gap-1"><Lock size={11} /> Giocata nascosta</div>
                      : <div className="text-xs text-[#94A3B8]">{e.won_count}/{e.events_count} azzeccate{e.stake > 0 ? ` · punt. ${e.stake}€` : ""}</div>}
                  </div>
                  {payout && typeof e.payout === "number" && (
                    <span data-testid={`tkr-won-${e.user_id || i}`} className={`text-sm font-extrabold ${e.winnings_net > 0 ? "text-[#00D95F]" : "text-[#94A3B8]"}`}>
                      {e.winnings_net > 0 ? `+${e.winnings_net.toFixed(2)}€` : `${e.payout.toFixed(2)}€`}
                    </span>
                  )}
                  {board?.bonus?.enabled && e.bonus_won && <span data-testid={`tkr-bonus-won-${e.user_id || i}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40"><Target size={11} /> Bonus</span>}
                  {i === 0 && Number(e.total) > 0 && <Trophy size={16} className="text-[#F59E0B]" />}
                  {isLast && entries.length > 1 && !payout && <Beer size={16} className="text-[#F59E0B]" />}
                  <span className="font-extrabold text-[#F59E0B] w-12 text-right">{Number(e.total || 0).toFixed(2)}</span>
                  <ChevronDown size={16} className={`text-[#94A3B8] transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div data-testid={`tkr-breakdown-${e.user_id || i}`} className="px-4 pb-3 space-y-1.5">
                    {payout && typeof e.payout === "number" && (
                      <div className="rounded-lg bg-[#0F1216] border border-white/10 p-2.5 text-xs grid grid-cols-2 gap-x-4 gap-y-1 mb-1">
                        <div className="flex justify-between"><span className="text-[#94A3B8]">Puntata</span><b>{Number(e.stake || 0).toFixed(2)}€</b></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8]">Vincita lorda</span><b>{Number(e.winnings_gross || 0).toFixed(2)}€</b></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8]">Commissione</span><b className="text-[#F59E0B]">-{Number(e.commission || 0).toFixed(2)}€</b></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8]">Vincita netta</span><b className="text-[#00D95F]">{Number(e.winnings_net || 0).toFixed(2)}€</b></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8]">Rimborso</span><b>{Number(e.refund || 0).toFixed(2)}€</b></div>
                        <div className="flex justify-between border-t border-white/10 pt-1 mt-0.5"><span className="text-white font-bold">Totale</span><b className="text-[#00D95F]">{Number(e.payout || 0).toFixed(2)}€</b></div>
                      </div>
                    )}
                    {(e.breakdown || []).length === 0 && (
                      <div className="text-xs text-[#64748B] flex items-center gap-1">
                        {e.hidden ? <><Lock size={12} /> Giocata nascosta fino alla scadenza della giornata.</> : "Nessun evento."}
                      </div>
                    )}
                    {(e.breakdown || []).map((b, j) => {
                      const evaluated = hasResults && b.matched_fixture;
                      const isVoid = !!b.void;
                      const isWin = b.won && !isVoid;
                      const isLose = evaluated && !b.won && !isVoid;
                      const cls = isVoid ? "border-white/10 bg-[#0F1216]" : isWin ? "border-[#00D95F]/40 bg-[#00D95F]/10" : isLose ? "border-[#EF4444]/40 bg-[#EF4444]/10" : "border-white/10 bg-[#0F1216]";
                      const voidLabel = b.suspended ? "NULLA" : b.postponed && b.score === "ESCL." ? "ESCL." : "RINV.";
                      return (
                        <div key={j} data-testid={`tkr-event-${e.user_id || i}-${j}`} className={`rounded-lg border p-2.5 text-sm flex items-center gap-2 ${cls}`}>
                          <div className="flex-1 min-w-0">
                            <div className="truncate flex items-center gap-1.5">{b.home_team} <span className="text-[#94A3B8]">-</span> {b.away_team}
                              {b.suspended && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40 font-bold shrink-0">SOSPESA</span>}
                            </div>
                            <div className="text-xs text-[#94A3B8]">Pronostico <b className="text-[#F59E0B]">{b.prediction}</b> @ {isVoid ? "1.00" : Number(b.odd).toFixed(2)}{isVoid && <span className="text-[#64748B]"> (orig. {Number(b.odd).toFixed(2)})</span>}</div>
                          </div>
                          {b.score && <span className="text-xs text-[#94A3B8]">{b.score}</span>}
                          {isVoid ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-[#94A3B8] font-bold">{voidLabel}</span>
                            : isWin ? <CheckCircle2 size={16} className="text-[#00D95F]" />
                            : isLose ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF4444]/25 text-[#EF4444] font-bold">PERSA</span>
                            : <span className="text-[10px] text-[#64748B]">—</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {payout && (
          <div data-testid="tkr-payout-summary" className="rounded-xl border border-[#00D95F]/30 bg-[#00D95F]/5 p-4 mt-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2 font-extrabold text-[#00D95F]"><Wallet size={16} /> Riepilogo premi</div>
              <button data-testid="tkr-pdf-btn" onClick={downloadPdf} className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/15 border border-white/15 rounded-md px-3 py-1.5"><Download size={13} /> Scarica PDF</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center justify-between bg-[#0F1216] rounded-lg px-3 py-2"><span className="text-[#94A3B8]">Montepremi</span><b className="text-white" data-testid="tkr-sum-montepremi">{payout.montepremi.toFixed(2)}€</b></div>
              <div className="flex items-center justify-between bg-[#0F1216] rounded-lg px-3 py-2"><span className="text-[#94A3B8]">Vincite nette</span><b className="text-[#00D95F]">{payout.winnings_net_total.toFixed(2)}€</b></div>
              <div className="flex items-center justify-between bg-[#0F1216] rounded-lg px-3 py-2"><span className="text-[#94A3B8]">Commissione ({Math.round((payout.commission_rate || 0.1) * 100)}%)</span><b className="text-[#F59E0B]">{payout.commission_total.toFixed(2)}€</b></div>
              <div className="flex items-center justify-between bg-[#0F1216] rounded-lg px-3 py-2"><span className="text-[#94A3B8]">Soldi restituiti</span><b className="text-white">{payout.refund_total.toFixed(2)}€</b></div>
            </div>
            {entries.some((e) => Number(e.refund) > 0) && (
              <div data-testid="tkr-refund-breakdown" className="mt-2 rounded-lg bg-[#0F1216] px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-[#94A3B8] font-bold mb-1.5">Soldi restituiti · dettaglio</div>
                <div className="space-y-1">
                  {entries.filter((e) => Number(e.refund) > 0).map((e, i) => (
                    <div key={e.user_id || i} data-testid={`tkr-refund-${e.user_id || i}`} className="flex items-center justify-between">
                      <span className="text-[#E2E8F0] truncate">{e.nickname}</span>
                      <b className="text-white shrink-0">{Number(e.refund).toFixed(2)}€</b>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-[10px] text-[#64748B] mt-2">Montepremi = vincite nette + commissione + soldi restituiti</div>
          </div>
        )}
      </div>
    </div>
  );
}
